/** Async primitives: sleep, timeout, retry, and a cancellable task handle. */

export function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    }
    signal?.addEventListener?.('abort', onAbort, { once: true });
  });
}

export function withTimeout(promise, ms, label = 'operation') {
  let timer;
  return Promise.race([
    promise.finally(() => clearTimeout(timer)),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    }),
  ]);
}

export async function retry(fn, { attempts = 3, delayMs = 800, factor = 2, signal, onRetry } = {}) {
  let lastError;
  let wait = delayMs;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await fn(i);
    } catch (error) {
      lastError = error;
      if (signal?.aborted) throw error;
      if (i === attempts - 1) break;
      onRetry?.(error, i + 1);
      await sleep(wait, signal);
      wait *= factor;
    }
  }
  throw lastError;
}

/** A simple cancellable-task wrapper used by long renders. */
export function createTask() {
  const controller = new AbortController();
  let cancelReason = null;
  return {
    signal: controller.signal,
    get cancelled() {
      return controller.signal.aborted;
    },
    get reason() {
      return cancelReason;
    },
    cancel(reason = 'cancelled') {
      cancelReason = reason;
      controller.abort();
    },
    throwIfCancelled() {
      if (controller.signal.aborted) {
        throw new DOMException(cancelReason ?? 'Cancelled', 'AbortError');
      }
    },
  };
}

export function isAbortError(error) {
  return error?.name === 'AbortError' || error?.name === 'NotAllowedError';
}
